import { Step, useComputed, useSnackbar } from "@homebound/beam";
import { fragment, useFormState } from "@homebound/form-state";
import kebabCase from "lodash/kebabCase";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { StepperProvider, useStepperContext } from "src/components/stepper";
import { WizardStep } from "src/components/stepper/useStepperWizard/useStepperWizard";
import { ReadyPlanOptionGroupPersonalizationType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { StepNav } from "src/routes/libraries/design-catalog/personalization-option/components/StepNav";
import {
  useEditPersonalizationOptionWizard_CombinationsQuery,
  useEditPersonalizationOptionWizardQuery,
  useSavePersonalizationRpogWizardMutation,
} from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import { DetailsStep } from "src/routes/libraries/design-catalog/personalization-option/steps/DetailsStep";
import { VariantsStep } from "src/routes/libraries/design-catalog/personalization-option/steps/VariantsStep";
import {
  addExistingMaterials,
  buildRpoIdToRpogIdMap,
  formConfig,
  initializeOptions,
  mapToForm,
} from "src/routes/libraries/design-catalog/personalization-option/utils";
import { IdOrAddParams } from "src/routes/routesDef";
import { createDesignPackageUrl } from "src/RouteUrls";
import { fail } from "src/utils";

/** Creates/updates Personalization Options via a multi-step stepper (v2 of EditPersonalizationOptionPage). */
export function EditPersonalizationOptionWizard() {
  const searchParams = new URLSearchParams(useLocation().search);
  const forTli = searchParams.get("forTli") || undefined;
  const returnTo = searchParams.get("returnTo") || undefined;
  const {
    idOrAdd: rpogId,
    designPackageId: dpId,
    designPackageVersionId: rpavId,
  } = useRouteParams<IdOrAddParams & { designPackageId: string; designPackageVersionId: string }>();
  const { triggerNotice } = useSnackbar();
  const navigate = useNavigate();
  const isAdd = rpogId === "add";

  const query = useEditPersonalizationOptionWizardQuery({
    variables: {
      dpId,
      rpavId,
      rpogId,
      isAdd, // We'll @skip querying for the rpogId when `isAdd` is true
      forTliId: forTli ?? "",
      hasTliId: !!forTli, // We use `forTli` in both new-option & add-to-option flows
    },
  });
  const [mutate] = useSavePersonalizationRpogWizardMutation();
  const formState = useFormState({
    config: formConfig,
    init: { query, map: (data) => mapToForm(data, isAdd, forTli) },
    readOnly: query.data?.designPackage.canEdit.allowed === false,
  });
  const personalizationType = useComputed(() => formState.type.value, [formState]);
  const saveDisabled = useComputed(
    () =>
      disableBasedOnPotentialOperation(query.data?.designPackage.canEdit) ||
      !formState.dirty ||
      (!formState.valid && (formState.errors.toString() || true)),
    [formState, query.data],
  );

  useEffect(() => {
    const tli = query.data?.takeoffLineItem;
    if (isAdd && personalizationType && tli) {
      // When adding, use the initial placeholder's products as the initial materials
      // (Before careful inside this impl to not nuke wip data, as the useEffect can easily be called twice)
      initializeOptions(personalizationType, formState, tli);
    } else if (!isAdd && tli) {
      // When add-to-option-ing, fill in the existing cards
      const rpoIdToRpogId = buildRpoIdToRpogIdMap(query.data?.designPackage.availableTargetGroups ?? []);
      addExistingMaterials(formState, tli, rpoIdToRpogId);
      // This `useEffect` can be called twice, so only add if it doesn't exist yet
      if (!formState.placeholders.value.some((p) => p.id === tli.id)) {
        formState.placeholders.set([...formState.placeholders.value, { id: tli.id, data: fragment(tli) }]);
      }
    }
  }, [formState, personalizationType, isAdd, query]);

  async function save() {
    const { placeholders, productOptions, packageOptions, ...rest } = formState.changedValue;
    const { data, errors } = await mutate({
      variables: {
        input: {
          ...(isAdd ? { designPackageId: dpId } : {}),
          ...rest,
          // This is exhaustive; any placeholder IDs that we leave out will be unhooked
          controlledSlots: formState.placeholders.value.map((tli) => ({
            placeholderId: tli.id,
            order: tli.order ?? null,
          })),
          ...(formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package
            ? packageOptions
              ? {
                  // This is exhaustive; any package options that we leave out will be unhooked
                  packageOptions: formState.packageOptions.value?.map((po) => ({
                    ...po,
                    products: po.products?.map(({ productMaterial, ...p }) => ({
                      placeholderMaterialId: p.placeholderMaterialId,
                      productMaterialId: productMaterial?.id ?? null,
                      locationId: p.locationId,
                    })),
                  })),
                }
              : {}
            : {
                productOptions: formState.productOptions.value?.map((po) => ({
                  materialVariantId: po.materialVariantId,
                  personalizationTargetCombinations: po.personalizationTargetCombinations?.map((combo) => ({
                    options: combo.options,
                  })),
                })),
              }),
        },
      },
      refetchQueries: ["DesignPackageItemSlotEditor"],
    });
    if (!data || errors?.nonEmpty) return;
    // Use the version ID from the mutation response, which may be a new draft if the save
    // created one (e.g. when editing an active version with no existing draft).
    const newRpavId = data.savePersonalizationOptionGroup.version.id;
    triggerNotice({ message: `Option ${isAdd ? "created" : "updated"} successfully`, icon: "success" });
    // The wizard is a flow, so exit once saved: return to where the user came from, or fall back to
    // the design package page (on the version the save landed on, which may be a freshly-created draft).
    navigate(returnTo ?? createDesignPackageUrl(dpId, newRpavId));
  }

  const maybeRpog = query.data?.readyPlan?.optionGroups.first;
  const selectedTargetGroups = useComputed(
    () => formState.personalizationTargetGroupIds.value ?? [],
    [formState.personalizationTargetGroupIds],
  );
  // Get option combinations based on the selected target groups
  const combinationsQuery = useEditPersonalizationOptionWizard_CombinationsQuery({
    variables: { dpId, rpavId, targetGroupIds: selectedTargetGroups },
    skip: selectedTargetGroups.isEmpty,
  });

  const cancelUrl = returnTo || createDesignPackageUrl(dpId, rpavId);
  const targetGroups = useMemo(() => query.data?.designPackage.availableTargetGroups ?? [], [query.data]);
  const targetCombinations = useMemo(
    () => combinationsQuery.data?.designPackage.combinations ?? [],
    [combinationsQuery.data],
  );
  const title = maybeRpog ? `Edit ${maybeRpog.name} Option` : "Create New Option";

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        // Step 1 bundles the option details, the target groups it applies to, and its slots.
        label: "Details",
        render: () => (
          <DetailsStep
            formState={formState}
            title={title}
            personalizationTypes={query.data?.enumDetails.readyPlanOptionGroupPersonalizationType ?? []}
            isExisting={!!maybeRpog}
            targetGroups={targetGroups}
          />
        ),
      },
      {
        label: "Variants",
        render: () => (
          <VariantsStep
            formState={formState}
            title={title}
            targetGroups={targetGroups}
            targetCombinations={targetCombinations}
          />
        ),
      },
    ],
    [formState, query.data, title, targetGroups, targetCombinations, maybeRpog],
  );

  const initialSteps = useMemo<Step[]>(
    () => steps.map((step) => ({ ...step, state: "incomplete", value: kebabCase(step.label) })),
    [steps],
  );

  return (
    <StepperProvider steps={initialSteps} newLayout>
      <StepPicker steps={steps} />
      <StepNav cancelUrl={cancelUrl} saveDisabled={saveDisabled} onSave={save} saveLabel={isAdd ? "Create" : "Save"} />
    </StepperProvider>
  );
}

function StepPicker({ steps }: { steps: WizardStep[] }) {
  const { currentStep } = useStepperContext();
  const { render: Component } =
    steps.find((s) => s.label === currentStep.label) ?? fail(`No stepper step found for ${currentStep.label}`);
  return <>{Component()}</>;
}
